import React from "react";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Crown, Building2, Briefcase, Eye, User } from "lucide-react";

/**
 * RoleBadge Component
 * Displays a role badge with appropriate icon, color, and tooltip
 * 
 * Props:
 * - role: { name, scope, level, subsidiary_name, permissions }
 * - size: "sm" | "md" | "lg"
 * - onClick: Optional click handler
 */
const RoleBadge = ({ role, size = "sm", onClick }) => {
  if (!role) return null;

  const { name, scope, level, subsidiary_name, role_scope } = role;
  const actualScope = role_scope || scope || "subsidiary";

  // Determine icon based on role scope
  const getIcon = () => {
    if (actualScope === "platform") return Crown;
    if (actualScope === "company") return Building2;
    if (name === "Analyst") return Briefcase;
    if (name === "Viewer") return Eye;
    return User;
  };

  // Determine color based on role level/scope
  const getColorClass = () => {
    if (actualScope === "platform") return "bg-red-100 text-red-700 border-red-200";
    if (actualScope === "company") return "bg-blue-100 text-blue-700 border-blue-200";
    if (level <= 3) return "bg-purple-100 text-purple-700 border-purple-200";
    if (level === 4) return "bg-green-100 text-green-700 border-green-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  const Icon = getIcon();
  const colorClass = getColorClass();
  
  // Build display text
  const displayText = subsidiary_name ? `${name} (${subsidiary_name})` : name;
  
  // Build tooltip content
  const tooltipContent = (
    <div className="space-y-1">
      <div className="font-semibold">{name}</div>
      <div className="text-xs">
        <div>Scope: {actualScope === "platform" ? "Platform-wide" : actualScope === "company" ? "Company-wide" : "Subsidiary"}</div>
        {subsidiary_name && <div>Subsidiary: {subsidiary_name}</div>}
        {level && <div>Level: {level}</div>}
      </div>
    </div>
  );

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-1",
    lg: "text-base px-3 py-1.5"
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`${colorClass} ${sizeClasses[size]} flex items-center gap-1 ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
            onClick={onClick}
          >
            <Icon className="h-3 w-3" />
            <span className="max-w-[150px] truncate">{displayText}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default RoleBadge;
